import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTroubleshootTimesPage } from 'MeetingsLib/hooks/useTroubleshootTimes';
import { updateTroubleshootTimes } from '../redux/actions/TroubleshootActions';
import { getPortalId } from 'MeetingsLib/selectors/BookInfoSelectors';
import { isTroubleshootingSelector, getTroubleshootTimes } from 'MeetingsLib/selectors/TroubleshootingSelectors';
import TroubleshootTimesRecord from 'MeetingsBase/models/TroubleshootTimes';
import { jsx as _jsx } from "react/jsx-runtime";
const TroubleshootTimesContext = /*#__PURE__*/createContext({
  currentMonthOffset: 0,
  hasMore: false,
  loading: false,
  refetch: () => Promise.resolve(),
  troubleshootTimes: undefined,
  setCurrentMonthOffset: () => {},
  getNextMonthOffset: () => {},
  getPreviousMonthOffset: () => {}
});
export function TroubleshootTimesContextProvider({
  children,
  shouldSkipFirstPage,
  slug
}) {
  const [currentMonthOffset, setCurrentMonthOffset] = useState(0);
  const dispatch = useDispatch();
  const existingTroubleshootTimes = useSelector(getTroubleshootTimes);
  const isTroubleshooting = useSelector(isTroubleshootingSelector);
  // @ts-ignore unexpected field on state
  const userTimezone = useSelector(state => state.userTimezone);
  const existingTroubleshootTimesHasMore = useMemo(() => {
    return existingTroubleshootTimes && existingTroubleshootTimes.get('hasMore');
  }, [existingTroubleshootTimes]);
  const portalId = useSelector(getPortalId);
  // @ts-ignore __hsUserToken does not exist on window
  const hubspotUtk = window.__hsUserToken;
  const contactOwnerPrioritizedEmail = useSelector(
  // @ts-ignore untyped state
  state => state.emailFormState);
  const shouldSkip = !isTroubleshooting || shouldSkipFirstPage && currentMonthOffset === 0;
  const {
    troubleshootTimesPage,
    loading,
    error,
    refetch
  } = useTroubleshootTimesPage({
    slug,
    monthOffset: currentMonthOffset,
    contactEmail: contactOwnerPrioritizedEmail,
    portalId,
    hubspotUtk,
    timezone: userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  }, shouldSkip);
  const getNextMonthOffset = useCallback(() => {
    if (existingTroubleshootTimesHasMore && !loading) {
      setCurrentMonthOffset(previousOffset => previousOffset + 1);
    }
  }, [existingTroubleshootTimesHasMore, loading]);
  const getPreviousMonthOffset = useCallback(() => {
    if (currentMonthOffset > 0 && !loading) {
      setCurrentMonthOffset(previousOffset => previousOffset - 1);
    }
  }, [currentMonthOffset, loading]);
  useEffect(() => {
    if (troubleshootTimesPage && currentMonthOffset > 0) {
      const troubleshootimesPayload = TroubleshootTimesRecord.from(troubleshootTimesPage);
      dispatch(updateTroubleshootTimes(troubleshootimesPayload));
    }
  }, [currentMonthOffset, dispatch, troubleshootTimesPage]);
  return /*#__PURE__*/_jsx(TroubleshootTimesContext.Provider, {
    value: {
      currentMonthOffset,
      loading,
      hasMore: existingTroubleshootTimesHasMore,
      error,
      refetch,
      troubleshootTimes: troubleshootTimesPage,
      setCurrentMonthOffset,
      getNextMonthOffset,
      getPreviousMonthOffset
    },
    children: children
  });
}
export function useNextTroubleshootMonthOffset() {
  const context = useContext(TroubleshootTimesContext);
  return context.getNextMonthOffset;
}
export function usePreviousTroubleshootMonthOffset() {
  const context = useContext(TroubleshootTimesContext);
  return context.getPreviousMonthOffset;
}
export function useSetTroubleshootMonthOffset() {
  const context = useContext(TroubleshootTimesContext);
  return context.setCurrentMonthOffset;
}
export function useHasMoreTroubleshootPages() {
  const context = useContext(TroubleshootTimesContext);
  return context.hasMore;
}
export function useIsLoadingTroubleshootPage() {
  const context = useContext(TroubleshootTimesContext);
  return context.loading;
}
export function useTroubleshootPage() {
  const context = useContext(TroubleshootTimesContext);
  return context.troubleshootTimes;
}
export function useTroubleshootPageRefetch() {
  const context = useContext(TroubleshootTimesContext);
  return context.refetch;
}
export function useTroubleshootPageHasError() {
  const context = useContext(TroubleshootTimesContext);
  return context.error;
}